import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, pointStagger, FRAME_BEAT_MS, MARK_SWEEP_MS } from '../motion';
import { TIMING } from '../animationConfig';
import type { ChartSpec, ChartData } from '../copilotTypes';

type ScatterRegressionSpec = Extract<ChartSpec, { chart: 'scatter_regression' }>;
type ScatterRegressionData = Extract<ChartData, { chart: 'scatter_regression' }>;

export function buildScatterRegressionOption(_spec: ScatterRegressionSpec, data: ScatterRegressionData) {
  const { points, fit, units } = data;
  const xs = points.map((p) => p.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);

  // The regression line is fully described by `fit` (least-squares slope + intercept). A straight line
  // only needs its two endpoints, so we evaluate y = slope*x + intercept at the data's x-extent and let
  // ECharts draw the segment between them — it spans exactly the range the points cover, no extrapolation.
  const fitLine = [
    [minX, fit.slope * minX + fit.intercept],
    [maxX, fit.slope * maxX + fit.intercept],
  ];

  // Surface the correlation strength up front: Pearson r, rounded to 2dp, sits in the top-right corner
  // in muted text so it reads as an annotation, not a data series.
  const rLabel = `r = ${fit.r.toFixed(2)}`;

  return {
    ...ECHARTS_ANIM,
    // top:36 reserves a band above the plot for the y-axis unit (rendered at the axis 'end', top-left)
    // and lifts the r= annotation clear of the top gridline; right:18 keeps the last x tick off the edge.
    grid: { left: 4, right: 18, top: 36, bottom: 40, containLabel: true },
    tooltip: {
      trigger: 'item',
      // Two-line tooltip mirrors the histogram's/calendar's: a header line (the day this point is), then
      // the measured pair — each value bold and carried by its own metric unit (units.x / units.y).
      formatter: (p: any) => {
        // Scatter points carry their source row in `data` (so we can name the date); the fit line's
        // segments don't, so we render nothing for them.
        const d = p.data;
        if (!d || d.date == null) return '';
        return (
          `${d.date}<br/>` +
          `<strong>${Math.round(d.value[0]).toLocaleString()}</strong> ${units.x}<br/>` +
          `<strong>${Math.round(d.value[1]).toLocaleString()}</strong> ${units.y}`
        );
      },
    },
    xAxis: {
      type: 'value',
      name: units.x,
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      scale: true,
      // Compact large values (40000 → "40k") so a wide last tick can't be clipped at the grid's right
      // edge. Full precision still shows in the tooltip; this is axis chrome only.
      axisLabel: {
        hideOverlap: true,
        formatter: (v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`),
      },
    },
    yAxis: {
      type: 'value',
      name: units.y,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      scale: true,
    },
    graphic: [
      {
        type: 'text',
        right: 16,
        top: 8,
        style: {
          text: rLabel,
          fill: tokens.text.muted,
          fontFamily: tokens.fontFamily,
          fontSize: 12,
          fontWeight: 600,
        },
      },
    ],
    series: [
      {
        type: 'scatter',
        symbolSize: 9,
        // Each datum keeps its `value` pair (for axes/tooltip) plus its `date` (for the tooltip header).
        data: points.map((p) => ({ value: [p.x, p.y], date: p.date })),
        // Points materialize across the cloud in a bounded staggered sweep — even for a dense cohort scatter
        // (pointStagger never drops to 0, unlike the bar stagger). `n` is the point count captured here.
        animationDelay: pointStagger(points.length),
      },
      {
        type: 'line',
        // The fit line is an annotation, not a measured series — pin it to the brand color and disable
        // markers/tooltip so it reads as the trend overlay rather than another data category.
        data: fitLine,
        color: tokens.brand.cardinal,
        showSymbol: false,
        lineStyle: { color: tokens.brand.cardinal, width: 2 },
        tooltip: { show: false },
        silent: true,
        // Hold the line back until the whole point sweep has landed (frame beat + the sweep), then sweep
        // it THROUGH the settled cloud — the eye reads "points populate, then the trend draws through
        // them" as two beats rather than one muddled simultaneous draw. Honest: the line is the
        // least-squares fit, not measured data, so revealing it progressively distorts nothing. z:1 keeps
        // it above the points.
        animationDelay: FRAME_BEAT_MS + MARK_SWEEP_MS + 200,
        animationDuration: TIMING.trendSweepMs,
        z: 1,
      },
    ],
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const ScatterRegression = React.memo(function ScatterRegression({
  title,
  spec,
  data,
}: {
  title: string;
  spec: ScatterRegressionSpec;
  data: ScatterRegressionData;
}) {
  const option = React.useMemo(() => buildScatterRegressionOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
